import React, { useEffect, useMemo, useState } from "react";
import { Apple, Monitor, Download, Info } from "lucide-react";
import {
  DESKTOP_DOWNLOADS,
  DESKTOP_VERSION,
  DOWNLOAD_BASE,
} from "../config";

/**
 * Guess which build the visitor wants, so their platform is offered first.
 *
 * A guess, not a gate: every build stays visible and downloadable. Detection
 * from the user agent is unreliable — Apple Silicon in particular is not
 * distinguishable from Intel in JavaScript, and a Mac visitor who is hidden
 * from the build they actually need has no way back.
 */
function likelyPlatform() {
  if (typeof navigator === "undefined") return null;
  const ua = `${navigator.userAgent} ${navigator.platform || ""}`;
  if (/Win/i.test(ua)) return "windows";
  // Both Mac builds surface; ordering just puts Apple Silicon first, since
  // every Mac sold since 2020 is one.
  if (/Mac/i.test(ua)) return "mac-arm";
  return null;
}

// Which build a published file is, by its suffix. File names carry the release
// (AnalystBro_0.1.0-rc10_aarch64.dmg) so every release is a URL no cache has
// seen — fixed names made Cloudflare keep serving the previous build.
const SUFFIX = { windows: "_x64-setup.exe", "mac-arm": "_aarch64.dmg", "mac-intel": "_x64.dmg" };

/** The published release from latest.json (written by the desktop repo's
 * publish-installers.sh). Null until loaded or if unavailable; the built-in
 * names are the fallback. */
function useLatestRelease() {
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    fetch(`${DOWNLOAD_BASE}/latest.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && Array.isArray(j.files) && setLatest(j))
      .catch(() => {});
  }, []);
  return latest;
}

export default function DesktopDownloads() {
  const preferred = useMemo(likelyPlatform, []);
  const latest = useLatestRelease();

  const builds = useMemo(() => {
    const withFiles = DESKTOP_DOWNLOADS.map((b) => {
      const f = latest?.files.find((x) => x.name.endsWith(SUFFIX[b.id]));
      return f ? { ...b, file: f.name } : b;
    });
    if (!preferred) return withFiles;
    const isMac = preferred.startsWith("mac");
    return [...withFiles].sort((a, b) => {
      const rank = (x) => (x.id === preferred ? 0 : isMac && x.id.startsWith("mac") ? 1 : 2);
      return rank(a) - rank(b);
    });
  }, [preferred, latest]);

  return (
    <section id="download" className="scroll-mt-16 border-y border-[var(--ab-border)] bg-[var(--ab-bg-alt)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-28">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
              Get the desktop app
            </h2>
            <p className="mt-4 text-[var(--ab-muted)] max-w-2xl leading-relaxed">
              The research agent runs on your own machine — charts are rendered
              locally in your browser, and your filings corpus is reached over an
              authenticated local connection.
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs text-[var(--ab-faint)]">
            v{latest?.version || DESKTOP_VERSION}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {builds.map((b) => {
            const Icon = b.id === "windows" ? Monitor : Apple;
            const isPreferred = b.id === preferred;
            return (
              <a
                key={b.id}
                href={`${DOWNLOAD_BASE}/${b.file}`}
                // Same-origin, so no `download` round-trip issues; the browser
                // saves rather than navigates because of the content type.
                download
                className={`group rounded-xl border p-5 transition flex flex-col gap-2 bg-[var(--ab-surface)] ${
                  isPreferred
                    ? "border-[var(--ab-accent)] ring-4 ring-[var(--ab-accent-soft)]"
                    : "border-[var(--ab-border)] hover:border-[var(--ab-faint)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon size={20} className={isPreferred ? "text-[var(--ab-accent)]" : "text-[var(--ab-muted)]"} />
                  <span className="font-semibold">{b.label}</span>
                  {isPreferred && (
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[var(--ab-accent)]">
                      Detected
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--ab-muted)]">{b.detail}</p>
                {b.note && <p className="text-xs text-[var(--ab-faint)]">{b.note}</p>}
                <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ab-accent)] group-hover:gap-3 transition-all">
                  <Download size={16} /> Download
                </span>
              </a>
            );
          })}
        </div>

        <div className="mt-10 pt-6 border-t border-[var(--ab-border)] space-y-3 text-sm text-[var(--ab-muted)]">
          <p>
            You will also need{" "}
            <a href="https://www.google.com/chrome/" target="_blank" rel="noreferrer" className="text-[var(--ab-accent)] hover:underline">
              Google Chrome
            </a>{" "}
            for charts, and an AI coding agent — the app walks you through
            installing one on first run.
          </p>
          {/*
            Say this up front rather than letting it be a surprise. The builds are
            not yet signed with a paid developer certificate, so both systems warn
            on first open. A user who hits an unexplained "cannot be opened"
            dialog assumes the download is broken and does not come back.
          */}
          <details className="group">
            <summary className="cursor-pointer inline-flex items-center gap-2 hover:text-[var(--ab-text)]">
              <Info size={16} className="text-[var(--ab-accent)]" />
              First launch shows a security warning — here’s why
            </summary>
            <div className="mt-3 pl-6 space-y-1.5 leading-relaxed">
              <p>
                <span className="font-semibold text-[var(--ab-text)]">macOS:</span> right-click the app and
                choose <span className="font-semibold text-[var(--ab-text)]">Open</span>, then confirm.
                Double-clicking shows “cannot be opened” until you do this once.
              </p>
              <p>
                <span className="font-semibold text-[var(--ab-text)]">Windows:</span> SmartScreen may show
                “Windows protected your PC” — choose{" "}
                <span className="font-semibold text-[var(--ab-text)]">More info → Run anyway</span>.
              </p>
              <p>Both warnings are because the app is not yet code-signed. They stop once signing is in place.</p>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}
